// /src/layout/TopbarDatesWeather.tsx
import { useEffect, useState } from "react";

type Weather = {
  temperature?: number;   // °C
  weathercode?: number;   // WMO code
  city?: string;
  error?: string;
};

function formatGregorian(d = new Date()) {
  // Force Gregorian in Arabic locale
  return new Intl.DateTimeFormat("ar-SA-u-ca-gregory", { dateStyle: "full" }).format(d);
}
function formatHijri(d = new Date()) {
  // Islamic (Umm al-Qura)
  try {
    return new Intl.DateTimeFormat("ar-SA-u-ca-islamic-umalqura", { dateStyle: "full" }).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

// Arabic number formatting (e.g., 30 → ٣٠)
const formatNumberAr = (n: number) => new Intl.NumberFormat("ar-SA").format(n);

// WMO code → Arabic label (expanded)
function wmoLabel(code?: number) {
  if (code == null) return "";
  const groups: Record<string, number[]> = {
    "صافٍ": [0],
    "صحو إلى غائم جزئياً": [1, 2, 3],
    "ضباب/ضباب متجمّد": [45, 48],
    "رذاذ خفيف": [51],
    "رذاذ متوسط": [53],
    "رذاذ غزير": [55],
    "مطر خفيف": [61],
    "مطر متوسط": [63],
    "مطر غزير": [65],
    "ثلج خفيف": [71],
    "ثلج متوسط": [73],
    "ثلج غزير": [75],
    "زخات رذاذ": [56, 57],
    "زخات مطر خفيفة": [80],
    "زخات مطر متوسطة": [81],
    "زخات مطر غزيرة": [82],
    "زخات ثلج خفيفة": [85],
    "زخات ثلج غزيرة": [86],
    "عواصف رعدية": [95],
    "عواصف رعدية مع برد": [96, 99],
  };
  for (const [label, arr] of Object.entries(groups)) {
    if (arr.includes(code)) return label;
  }
  return "طقس";
}

export default function TopbarDatesWeather() {
  const [greg, setGreg] = useState<string>(formatGregorian());
  const [hijri, setHijri] = useState<string>(formatHijri());
  const [weather, setWeather] = useState<Weather>({});

  // Refresh dates hourly (midnight rollover safety)
  useEffect(() => {
    const id = setInterval(() => {
      setGreg(formatGregorian());
      setHijri(formatHijri());
    }, 60 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const fallback = { lat: 24.7136, lon: 46.6753 }; // Riyadh

    async function load(lat: number, lon: number) {
      // 1) Weather (Open-Meteo; keyless)
      try {
        const r = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current_weather=true&timezone=auto&forecast_days=1`
        );
        if (!r.ok) throw new Error("weather_http_error");
        const j = await r.json();
        const cw = j?.current_weather || {};
        setWeather((w) => ({
          ...w,
          temperature: typeof cw.temperature === "number" ? cw.temperature : undefined,
          weathercode: typeof cw.weathercode === "number" ? cw.weathercode : undefined,
        }));
      } catch {
        setWeather((w) => ({ ...w, error: "weather_failed" }));
      }

      // 2) City label — try Open-Meteo reverse geocoding first…
      try {
        const r = await fetch(
          `https://geocoding-api.open-meteo.com/v1/reverse?latitude=${lat}&longitude=${lon}&language=ar&format=json`
        );
        if (!r.ok) throw new Error("reverse_geocode_http_error");
        const j = await r.json();
        const res = j?.results?.[0];
        const name = res?.name as string | undefined;
        const admin = res?.admin1 as string | undefined;
        const city = name || admin;
        if (city) {
          setWeather((w) => ({ ...w, city }));
          return;
        }
        throw new Error("reverse_geocode_empty");
      } catch {
        // …fallback to IP-based city (CORS-friendly, keyless)
        try {
          const ipr = await fetch("https://ipapi.co/json/");
          if (ipr.ok) {
            const ipj = await ipr.json();
            const city = ipj?.city || ipj?.region || ipj?.country_name;
            if (city) setWeather((w) => ({ ...w, city }));
          }
        } catch {
          /* ignore; city will remain undefined */
        }
      }
    }

    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => load(pos.coords.latitude, pos.coords.longitude),
        () => load(fallback.lat, fallback.lon),
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 10 * 60 * 1000 }
      );
    } else {
      load(fallback.lat, fallback.lon);
    }
  }, []);

  const hasTemp = typeof weather.temperature === "number";
  const hasCode = typeof weather.weathercode === "number";
  const label = hasCode ? wmoLabel(weather.weathercode) : "";

  return (
    <div className="flex items-center gap-4 text-gray-600">
      <span>
        {weather.city ? `${weather.city} · ` : ""}
        {hasTemp ? `${formatNumberAr(Math.round(weather.temperature!))}°C` : "—"}
        {hasCode ? ` · ${label}` : ""}
      </span>
      <span className="text-gray-300">|</span>
      <span title="التاريخ الميلادي">{greg}</span>
      <span className="text-gray-300">/</span>
      <span title="التاريخ الهجري">{hijri}</span>
    </div>
  );
}
